import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import React, { useContext } from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  ExpandedMultiSelect,
  FlexCol,
  FlexRow,
} from '../../../../common/components/v2';
import { CLR_HALT_REASONS_OPTIONS } from '../../../../common/constants/clrHaltReasons';
import { FeatureFlags } from '../../../../common/constants/featureFlags';
import { FeatureFlagsContext } from '../../../../common/featureFlagsWrapper';

interface ClrCheckboxBaseProps {
  showHaltReasons?: boolean;
}

// Props for react-hook-form usage
interface ClrCheckboxControlledProps extends ClrCheckboxBaseProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  triggerClrName?: string;
  haltClrReasonsName?: string;
  triggerClr?: never;
  setTriggerClr?: never;
  haltClrReasons?: never;
  setHaltClrReasons?: never;
}

// Props for direct state usage
interface ClrCheckboxUncontrolledProps extends ClrCheckboxBaseProps {
  triggerClr: boolean;
  setTriggerClr: (value: boolean) => void;
  haltClrReasons: string[];
  setHaltClrReasons: (value: string[]) => void;
  control?: never;
  triggerClrName?: never;
  haltClrReasonsName?: never;
}

type ClrCheckboxProps =
  | ClrCheckboxControlledProps
  | ClrCheckboxUncontrolledProps;

const TOOLTIP_TEXT =
  'Check this box to prevent the case from being sent to carriers for Closed Loop Rescission (CLR).';

export const ClrCheckbox: React.FC<ClrCheckboxProps> = props => {
  const { flags } = useContext(FeatureFlagsContext);
  const isHaltClrEnabled =
    flags[FeatureFlags.UW_NORA_HALT_CLR_ENABLED] || false;

  // Don't render if feature is disabled
  if (!isHaltClrEnabled) {
    return null;
  }

  const isControlled = 'control' in props && props.control !== undefined;

  if (isControlled) {
    const {
      control,
      triggerClrName = 'triggerClr',
      haltClrReasonsName = 'haltClrReasons',
    } = props as ClrCheckboxControlledProps;

    return (
      <Controller
        control={control}
        name={triggerClrName}
        render={({ field: triggerField }) => (
          <Box sx={{ mb: '12px' }}>
            <FlexRow>
              <FlexCol xs={12}>
                <Box display="flex" alignItems="center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!triggerField.value}
                        onChange={e => triggerField.onChange(!e.target.checked)}
                      />
                    }
                    label="Halt Carrier Review (CLR)"
                  />
                  <Tooltip title={TOOLTIP_TEXT}>
                    <HelpOutlineIcon
                      fontSize="small"
                      color="action"
                      sx={{ ml: 1 }}
                    />
                  </Tooltip>
                </Box>
              </FlexCol>
            </FlexRow>
            {!triggerField.value && (
              <FlexRow>
                <FlexCol xs={12}>
                  <Controller
                    control={control}
                    name={haltClrReasonsName}
                    render={({ field, fieldState }) => (
                      <ExpandedMultiSelect
                        required
                        options={CLR_HALT_REASONS_OPTIONS}
                        label="CLR halt reasons"
                        error={fieldState.error?.message}
                        {...field}
                      />
                    )}
                  />
                </FlexCol>
              </FlexRow>
            )}
          </Box>
        )}
      />
    );
  }

  // Uncontrolled (direct state) version
  const { triggerClr, setTriggerClr, haltClrReasons, setHaltClrReasons } =
    props as ClrCheckboxUncontrolledProps;

  return (
    <Box sx={{ mb: '12px' }}>
      <FlexRow>
        <FlexCol xs={12}>
          <Box display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={!triggerClr}
                  onChange={e => setTriggerClr(!e.target.checked)}
                />
              }
              label="Halt Carrier Review (CLR)"
            />
            <Tooltip title={TOOLTIP_TEXT}>
              <HelpOutlineIcon fontSize="small" color="action" sx={{ ml: 1 }} />
            </Tooltip>
          </Box>
        </FlexCol>
      </FlexRow>
      {!triggerClr && (
        <FlexRow>
          <FlexCol xs={12}>
            <ExpandedMultiSelect
              required
              options={CLR_HALT_REASONS_OPTIONS}
              label="CLR halt reasons"
              value={haltClrReasons}
              onChange={setHaltClrReasons}
            />
          </FlexCol>
        </FlexRow>
      )}
    </Box>
  );
};

export default ClrCheckbox;
